import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Heading, Spinner, Text, VStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { useAppConfig, WithAuthorization } from "../../context";
import { useAiIngestRuns } from "../../hooks/query/questionnaire";
import { commitsBySessionId } from "../../utils/aiIngestTargets";
import { DataMappingProvider } from "./state/DataMappingContext";
import { useSession } from "./state/useSession";
import { useChat } from "./state/useChat";
import {
  createSession,
  deleteSession,
  getSession,
  getMessages,
  listSessions,
} from "./api/dataMappingClient";
import { SessionLayout } from "./SessionLayout";
import { isEmptySession, SessionPicker, SessionPickerLoading } from "./components/SessionPicker";

function SessionBootstrap() {
  const { sessionId, setSession, setFile } = useSession();
  const { setMessages } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const didBootstrap = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | needs-task | picker | ready
  const [sessions, setSessions] = useState([]);
  const [totalSessionCount, setTotalSessionCount] = useState(0);
  const [maxSessions, setMaxSessions] = useState(10);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const loadSession = useCallback(
    async (id) => {
      const info = await getSession(id);
      setSession(info.id, info.status, info);
      setFile(info.has_file, info.source_file_name);
      try {
        const { messages } = await getMessages(info.id);
        if (messages.length) setMessages(messages);
      } catch {
        // No history yet — fine.
      }
      const next = new URLSearchParams(searchParams);
      next.set("session", info.id);
      setSearchParams(next, { replace: true });
      setPhase("ready");
    },
    [setSession, setFile, setMessages, searchParams, setSearchParams],
  );

  // The data task this page is working against. Every session belongs to one, so without it there is
  // nothing to create — hence the explicit "needs-task" phase below rather than an auto-create.
  const questionnaireId = searchParams.get("questionnaireId");
  // Which part of the task the deep link came from (an AiIngestTarget name). Optional: a session without
  // one still works, the agent just has to ask. Setting it here saves that round trip and means the agent
  // reads the right output structure without guessing.
  const target = searchParams.get("target");

  // A session's own status (created/analyzing/...) is just the agent's workflow stage — it never reflects
  // whether it actually wrote anything into the task, since committing writes there directly. Cross-
  // referenced by sessionId against the task's ingest history for the picker below.
  const { data: ingestRuns } = useAiIngestRuns(questionnaireId);
  const commitsBySession = useMemo(() => commitsBySessionId(ingestRuns), [ingestRuns]);

  const startNewSession = useCallback(async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const s = await createSession(questionnaireId, target);
      setSession(s.id, s.status, s);
      const next = new URLSearchParams(searchParams);
      next.set("session", s.id);
      setSearchParams(next, { replace: true });
      setPhase("ready");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      setCreateError(
        status === 409 && detail
          ? detail
          : "Couldn't start a new session. Try again or delete an existing one.",
      );
    } finally {
      setIsCreating(false);
    }
  }, [setSession, searchParams, setSearchParams, questionnaireId, target]);

  const handleDelete = useCallback(async (id) => {
    await deleteSession(id);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setTotalSessionCount((prev) => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (didBootstrap.current || sessionId) return;
    didBootstrap.current = true;

    (async () => {
      const urlId = searchParams.get("session");
      if (urlId) {
        try {
          await loadSession(urlId);
          return;
        } catch {
          const next = new URLSearchParams(searchParams);
          next.delete("session");
          setSearchParams(next, { replace: true });
        }
      }

      // No URL session. Without a task there is nothing to create or list, so say so rather than
      // failing on the create call — the two previous fallbacks here auto-created a session, which now
      // surfaces as a confusing 422 on mount.
      const search = new URLSearchParams(window.location.search);
      const taskId = search.get("questionnaireId");
      if (!taskId) {
        setPhase("needs-task");
        return;
      }

      try {
        // Narrowed to the destination the user came from as well as the task — a session built for a
        // different target has a different output structure and nothing to do with this mapping. Read
        // from the URL rather than the `target` const above, for the same reason `taskId` is: this
        // effect runs once on mount with an empty dep array.
        const data = await listSessions(taskId, search.get("target"));
        setMaxSessions(data.max_sessions ?? 10);
        const all = data.sessions || [];
        setTotalSessionCount(all.length);
        // Untouched sessions (no file, agent never ran) are noise on a "resume where you left off" list —
        // nothing to resume. Hide them, and if that leaves nothing worth showing, skip the picker: reuse
        // the most recently opened empty one rather than creating yet another, or create one if truly none.
        const meaningful = all.filter((s) => !isEmptySession(s));
        if (meaningful.length > 0) {
          setSessions(meaningful);
          setPhase("picker");
        } else if (all.length > 0) {
          const mostRecent = [...all].sort(
            (a, b) => (b.last_accessed_at || 0) - (a.last_accessed_at || 0),
          )[0];
          await loadSession(mostRecent.id);
        } else {
          await startNewSession();
        }
      } catch (err) {
        // A failed list is not a reason to create a session — if it failed because of auth or tenancy,
        // creating would fail too, and the previous fallback turned that into an unexplained error.
        setCreateError(
          err?.response?.status === 401
            ? "Your sign-in has expired. Reload the page to continue."
            : "Couldn't load your sessions for this task. Try reloading the page.",
        );
        setPhase("picker");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "loading") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="80vh">
        <Spinner size="md" mr={3} />
        <Text color="gray.600">Loading...</Text>
      </Box>
    );
  }

  if (phase === "needs-task") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="80vh" px={6}>
        <VStack spacing={3} maxW="480px" textAlign="center">
          <Heading size="md" color="gray.700">
            Open this from a data task
          </Heading>
          <Text fontSize="body.s" color="gray.500">
            AI Data Mapping works against a specific carbon data task, so it needs to know which one.
            Open it using the <strong>AI Mapping</strong> button on a task&apos;s data grid.
          </Text>
        </VStack>
      </Box>
    );
  }

  if (phase === "picker") {
    return (
      <Box maxW="640px" mx="auto" mt={10} px={4}>
        <Heading size="md" mb={2} color="darkTeal">
          Your sessions
        </Heading>
        <Text color="gray.600" mb={4}>
          Continue where you left off, or start a new session.
        </Text>
        <SessionPicker
          sessions={sessions}
          totalCount={totalSessionCount}
          maxSessions={maxSessions}
          onContinue={(s) => loadSession(s.id)}
          onDelete={handleDelete}
          onNew={startNewSession}
          isCreating={isCreating}
          createError={createError}
          commitsBySessionId={commitsBySession}
        />
      </Box>
    );
  }

  if (!sessionId) {
    return <SessionPickerLoading />;
  }

  return <SessionLayout />;
}

function AiDataMappingPage() {
  // Collapse the left nav while on this page so the data-mapping workspace has
  // maximum horizontal room. Restore the user's previous state on unmount.
  const { storedSidebarState, setStoredSidebarState } = useAppConfig();
  const restoreSidebarRef = useRef(storedSidebarState);

  useEffect(() => {
    restoreSidebarRef.current = storedSidebarState;
    setStoredSidebarState(false);
    return () => setStoredSidebarState(restoreSidebarRef.current);
    // Run once on mount; capture the entry state to restore on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edge-to-edge layout: skip the standard <Page> wrapper (which adds the
  // animated padding container used by every other page) so the gray chat
  // panel can sit flush against the app's left nav.
  return (
    <DataMappingProvider>
      <SessionBootstrap />
    </DataMappingProvider>
  );
}

// Wrapped like every other gated page (53 of them use this HOC). Without it the route was reachable by
// any authenticated user who knew the URL: `requiredPermissions` and `requiredModules` for this page live
// under `linkProps` in paths.js, which only the sidebar consults — so the sidebar hid the link while the
// route itself was open. `componentRequiredPermissions` and `module` are passed from the route element.
export default WithAuthorization(AiDataMappingPage);
